import { useCallback } from 'react';
import { useMutation } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import { toNumber, toString } from 'lodash';

import { api } from '@/api';
import { RAW_DATE_FORMAT } from '@/common/constants';
import {
  API_URL,
  IClaimActorDtoClaimActorTypeEnum,
  IClaimInfoResponseDto,
  ICreateClaimAggregatorDto,
  ICreateClaimAggregatorDtoClaimStatusTypeEnum,
  ICreateClaimAggregatorDtoPaymentTypeEnum,
  ICreateClaimAggregatorDtoPayToTypeEnum,
  ICreateClaimAggregatorDtoSourceEnum,
} from '@/dtos';
import { useAuthStore } from '@/modules/user/stores';

export type CreatedClaimDto = IClaimInfoResponseDto;

export interface CreateClaimDto {
  amount?: string;
  providerId?: string;
  patientId?: string;
  dateOfService?: string;
  serviceId?: string;
  notes?: string;
  documentId?: string;
  lmnId?: string;
  accountNumber?: string;
  invoiceNumber?: string;
  ocrReceiptId?: string;
  paymentMethodId?: string;
  payToType?: ICreateClaimAggregatorDtoPayToTypeEnum;
  paymentType?: ICreateClaimAggregatorDtoPaymentTypeEnum;
}

const useCreateClaimQuery = () => {
  const { user } = useAuthStore();

  const formatClaim = useCallback((claimDto: CreateClaimDto): ICreateClaimAggregatorDto  => {
    const serviceFor = toString(user?.employee?.id) === claimDto.patientId
      ? {
          employee_id: toNumber(user?.employee?.id),
          claim_actor_type: user?.role as IClaimActorDtoClaimActorTypeEnum
        }
      : {
        dependent_id: toNumber(claimDto.patientId),
        claim_actor_type: IClaimActorDtoClaimActorTypeEnum.DEPENDENT
      };

    return {
      // required details
      requested_amount: claimDto.amount ? parseFloat(claimDto.amount) : undefined,
      provider_id: claimDto.providerId ? toNumber(claimDto.providerId) : undefined,
      service_id: claimDto.serviceId ? toNumber(claimDto.serviceId): undefined,
      local_date_service_on: (claimDto?.dateOfService
        ? dayjs(claimDto.dateOfService).format(RAW_DATE_FORMAT)
        : undefined) as unknown as Date,
      service_for: serviceFor,

      // optional details
      ...(claimDto.notes ? { employee_notes: claimDto.notes } : {}),
      employee_provider_acc: claimDto?.accountNumber,
      provider_invoice: claimDto?.invoiceNumber,

      // payment details
      pay_to_type: claimDto.payToType,
      payment_method_id: claimDto.paymentMethodId ? toNumber(claimDto.paymentMethodId) : undefined,
      payment_type: claimDto.paymentType,
  
      // document details
      document_id: claimDto?.documentId ? toNumber(claimDto?.documentId) : undefined,
      ocr_receipt_id: claimDto?.ocrReceiptId ? toNumber(claimDto?.ocrReceiptId) : undefined,
      
      // general details
      employee_id: user?.employee?.id ? toNumber(user?.employee?.id) : undefined,
      organization_id: user?.organization?.id ? toNumber(user?.organization?.id) : undefined,
      organization_path: user?.organization?.path,
      claim_status_type: ICreateClaimAggregatorDtoClaimStatusTypeEnum.PROCESSING,
      source: ICreateClaimAggregatorDtoSourceEnum.WEBSITE,
      ...claimDto?.lmnId ? { lmn_ids: [toNumber(claimDto?.lmnId)] } : {},
    };
  }, [user?.organization, user?.employee, user?.role]);

  const { mutateAsync, isLoading } = useMutation((claimDto: CreateClaimDto) => api.post(API_URL.CLAIMS, formatClaim(claimDto)));
  
  const createClaim = useCallback(async (claimDto: CreateClaimDto): Promise<CreatedClaimDto> => {
    const response = await mutateAsync(claimDto);
    return response.data;
  }, [mutateAsync]);
  
  return {
    createClaim,
    isCreateClaimLoading: isLoading,
  };
};

export default useCreateClaimQuery;
